<section class="sim-page">
  <header class="page-header">
    <div>
      <p class="eyebrow">R&amp;D Lab</p>
      <h1>AI Call Simulator</h1>
      <p>Test the GRMS hermes-agent R&amp;D audio endpoint in the browser. Voice input is sent to GRMS, GRMS transcribes it with Whisper, passes the transcript to Hermes, and returns both text values here.</p>
    </div>
    <span class="status-pill" [class.online]="agentOnline === true" [class.offline]="agentOnline === false">
      <span class="dot"></span>
      {{ agentOnline === true ? 'Transcription online' : agentOnline === false ? 'Transcription offline' : 'Checking transcription…' }}
    </span>
  </header>

  <div class="sim-grid">
    <!-- LEFT: the "call" -->
    <section class="panel call-panel">
      <div class="panel-title">
        <span class="material-icons">support_agent</span>
        <h2>The Call</h2>
      </div>

      <div class="field-row">
        <label>
          <span>Language</span>
          <select [(ngModel)]="language">
            <option value="ur">Urdu</option>
            <option value="en">English</option>
          </select>
        </label>
        <label>
          <span>Personality</span>
          <select [(ngModel)]="personality" [disabled]="busy || recording">
            <option *ngFor="let p of personalities" [value]="p.value">{{ p.label }}</option>
          </select>
        </label>
        <label>
          <span>Workflow</span>
          <select [(ngModel)]="simulatorBackend" [disabled]="busy || recording" (change)="pingAgent()">
            <option *ngFor="let b of simulatorBackends" [value]="b.value">{{ b.label }}</option>
          </select>
        </label>
        <label class="grow">
          <span>Session</span>
          <input [(ngModel)]="sessionId" />
        </label>
      </div>
      <p class="personality-hint">
        {{ simulatorBackendDescription() }}<br />
        Tone/style test only: {{ personalityDescription() }}
      </p>

      <div class="composer">
        <textarea [(ngModel)]="questionText"
                  (keydown.enter)="$event.preventDefault(); askText()"
                  [disabled]="busy || recording"
                  placeholder="Type what the citizen says… or use the mic"></textarea>
        <div class="composer-actions">
          <button class="btn primary" (click)="askText()" [disabled]="busy || recording || !questionText.trim()">
            <span class="material-icons">send</span> Ask
          </button>
          <button class="btn mic" [class.recording]="recording" (click)="toggleRecording()" [disabled]="busy && !recording">
            <span class="material-icons">{{ recording ? 'stop' : 'mic' }}</span>
            {{ recording ? 'Stop &amp; send' : 'Speak' }}
          </button>
        </div>
      </div>

      <div class="examples">
        <span class="examples-label">Try:</span>
        <button *ngFor="let ex of examples" class="chip" (click)="useExample(ex)" [disabled]="busy || recording">{{ ex }}</button>
      </div>

      <p class="error" *ngIf="errorMessage">{{ errorMessage }}</p>
      <p class="listening" *ngIf="recordingStatus"><span class="material-icons">hearing</span> {{ recordingStatus }}</p>
      <div class="capture-debug" *ngIf="lastCaptureDebug">
        <span>Last mic capture: {{ lastCaptureDebug }}</span>
        <button type="button" class="link" *ngIf="lastCaptureUrl" (click)="replayCapture()">Play capture</button>
        <button type="button" class="link" (click)="clearCapturePreview()">Clear</button>
      </div>
      <p class="thinking" *ngIf="busy"><span class="spinner"></span> transcribing and asking Hermes…</p>

      <!-- conversation log -->
      <div class="turns" *ngIf="turns.length">
        <div class="turns-head">
          <h3>Conversation</h3>
          <button class="link" (click)="clearConversation()">Clear</button>
        </div>

        <article class="turn" *ngFor="let t of turns">
          <div class="q">
            <span class="material-icons">{{ t.via === 'voice' ? 'mic' : 'person' }}</span>
            <p>{{ t.question }}</p>
          </div>
          <div class="a">
            <div class="badges">
              <span class="badge" [ngClass]="'k-' + sourceKind(t.source)">{{ sourceLabel(t.source) }}</span>
              <span class="badge muted" *ngIf="t.source === 'QNA_MEMORY' && t.similarity !== null">match {{ pct(t.similarity) }}</span>
              <span class="badge muted" *ngIf="t.source === 'QNA_GETANSWER' && t.score !== null">score {{ pct(t.score) }}</span>
              <span class="badge saved" *ngIf="t.saved">saved ✓</span>
              <button class="badge play" *ngIf="t.audioUrl" (click)="replay(t)"><span class="material-icons">volume_up</span> replay</button>
            </div>
            <p class="answer">{{ t.answer }}</p>
            <div class="timings" *ngIf="timingList(t.timings).length">
              <span *ngFor="let m of timingList(t.timings)">{{ m.k }} {{ m.v }}ms</span>
              <span class="total">total {{ totalMs(t.timings) }}ms</span>
            </div>
          </div>
        </article>
      </div>
    </section>

    <!-- RIGHT: live flow + what the agent remembers -->
    <div class="right-col">

    <aside class="panel flow-panel" *ngIf="flow && flow.workflow">
      <div class="panel-title">
        <span class="material-icons">account_tree</span>
        <h2>Live Flow</h2>
      </div>
      <p class="flow-line"><span class="k">workflow</span><span class="v">{{ flow.workflow }}</span></p>
      <p class="flow-line" *ngIf="flow.step"><span class="k">step</span><span class="v">{{ flow.step }}</span></p>
      <p class="flow-line" *ngIf="flow.complaintNumber"><span class="k">complaint&nbsp;#</span><span class="v">{{ flow.complaintNumber }}</span></p>

      <!-- registration step tracker -->
      <ol class="steps" *ngIf="flow.workflow === 'REGISTER'">
        <li *ngFor="let s of regSteps" [ngClass]="'st-' + fieldState(s.key)">
          <span class="tick">{{ fieldState(s.key) === 'done' ? '✓' : fieldState(s.key) === 'active' ? '▸' : '·' }}</span>
          <span class="nm">{{ s.label }}</span>
          <span class="val" *ngIf="flow.fields && flow.fields[s.key]">{{ flow.fields[s.key] }}</span>
        </li>
      </ol>
    </aside>

    <aside class="panel mem-panel">
      <div class="panel-title">
        <span class="material-icons">psychology</span>
        <h2>Agent Memory</h2>
      </div>
      <p class="hint">The context store the agent builds. First ask → getAnswer + remember. Same question again → answered straight from here, no getAnswer.</p>

      <div class="mem-head">
        <span class="count">{{ memory.length }} remembered</span>
        <button class="link" (click)="clearMemory()" [disabled]="!memory.length">Forget all</button>
      </div>

      <p class="empty" *ngIf="!memory.length">Nothing remembered yet — ask a question the agent can confidently answer.</p>

      <ul class="mem-list">
        <li *ngFor="let m of memory">
          <p class="mq">{{ m.question }}</p>
          <p class="ma">{{ m.answer }}</p>
          <span class="ms" *ngIf="m.score !== undefined">cached at score {{ pct(m.score) }}</span>
        </li>
      </ul>
    </aside>

    </div><!-- /right-col -->
  </div>
</section>
